/**
 * Zentrale Utility-Datei für alle Datums- und Zeitberechnungen im Optimox Manager.
 * Nutzt 'date-fns' für zeitzonen- und schaltjahresichere Berechnungen.
 */
import { 
    format, 
    getISOWeek, 
    isSameDay as dateFnsIsSameDay, 
    roundToNearestMinutes,
    differenceInCalendarDays
} from 'date-fns';
import { de } from 'date-fns/locale';

/**
 * Gibt die Kalenderwoche (ISO) für ein Datum zurück.
 * @param {Date|string|number} date - Das zu prüfende Datum.
 * @returns {number} Die Kalenderwoche.
 */
export function getWeekNumber(date) {
    if (!date) return null;
    return getISOWeek(new Date(date));
}

/**
 * Formatiert ein Datum in eine deutsche Uhrzeit (z.B. "08:30").
 * @param {Date|string|number} date - Das Datum.
 * @returns {string} Formattierte Uhrzeit.
 */
export function formatTime(date) {
    if (!date) return '';
    return format(new Date(date), 'HH:mm', { locale: de });
}

/**
 * Prüft ob zwei Daten auf den selben Kalendertag fallen.
 * @param {Date|string|number} dateLeft 
 * @param {Date|string|number} dateRight 
 * @returns {boolean} 
 */
export function isSameDay(dateLeft, dateRight) {
    if (!dateLeft || !dateRight) return false;
    return dateFnsIsSameDay(new Date(dateLeft), new Date(dateRight));
}

/**
 * Erzeugt einen Datenbank-kompatiblen Key (YYYY-MM-DD).
 * @param {Date|string|number} date 
 * @returns {string} 
 */
export function toDateKey(date) {
    if (!date) return '';
    return format(new Date(date), "yyyy-MM-dd");
}

/**
 * Konvertiert ein Datum in einen lokalen ISO-String (YYYY-MM-DD HH:mm:ss) 
 * ohne automatische UTC-Verschiebung durch die Standard-JS-Engine.
 * @param {Date|string|number} date 
 * @returns {string|null} 
 */
export function toLocalISO(date) {
    if (!date) return null;
    return format(new Date(date), "yyyy-MM-dd HH:mm:ss");
}

/**
 * Prüft, ob ein Einsatz ganztägig ist.
 * Erkennt sowohl explizite Flags als auch Mitternachts-Einsätze.
 * Unterstützt auch den Aufruf mit zwei Datums-Objekten: isAllDay(start, end).
 * @param {Object|Date} itemOrStart - Das Einsatz-Objekt ODER das Startdatum
 * @param {Date} [optionalEnd] - Das Enddatum (falls das erste Argument ein Datum ist)
 * @returns {boolean}
 */
export function isAllDay(itemOrStart, optionalEnd) {
    if (!itemOrStart) return false;

    let start, end, explicitAllDay;

    // Fall 1: Aufruf mit Objekt isAllDay(assignment)
    if (!(itemOrStart instanceof Date) && typeof itemOrStart === 'object') {
        explicitAllDay = itemOrStart.all_day ?? itemOrStart.is_all_day;
        if (explicitAllDay) return true;
        
        if (!itemOrStart.start_datetime || !itemOrStart.end_datetime) return false;
        start = new Date(itemOrStart.start_datetime);
        end = new Date(itemOrStart.end_datetime);
    } 
    // Fall 2: Aufruf mit Daten isAllDay(startDate, endDate)
    else {
        start = new Date(itemOrStart);
        end = optionalEnd ? new Date(optionalEnd) : start;
    }

    try {
        const sH = start.getHours();
        const sM = start.getMinutes();
        const eH = end.getHours();
        const eM = end.getMinutes();

        return sH === 0 && sM === 0 && (
            (eH === 23 && eM === 59) || 
            (eH === 0 && eM === 0)
        );
    } catch (e) {
        return false;
    }
}

/**
 * Prüft, ob ein Einsatz über mehrere Kalendertage geht.
 * @param {Object} item 
 * @returns {boolean}
 */
export function isMultiDay(item) {
    if (!item || !item.start_datetime || !item.end_datetime) return false;
    return differenceInCalendarDays(new Date(item.end_datetime), new Date(item.start_datetime)) !== 0;
}

/**
 * Rundet ein Datum exakt auf 15-Minuten-Schritte, 
 * entfernt dabei Sekunden und Millisekunden konsequent.
 * @param {Date} date 
 * @returns {Date} 
 */
export function snapTo15Min(date) {
    if (!date) return new Date();
    const d = new Date(date);
    const rounded = roundToNearestMinutes(d, { nearestTo: 15, roundingMethod: 'round' });
    rounded.setSeconds(0, 0);
    return rounded;
}

export function formatDate(date) {
    if (!date) return '';
    return format(new Date(date), 'dd.MM.yyyy', { locale: de });
}

/**
 * Formatiert ein Datum kompakt (z.B. "19.04.26").
 * @param {Date|string|number} date 
 * @returns {string}
 */
export function formatDateCompact(date) {
    if (!date) return '';
    return format(new Date(date), 'dd.MM.yy');
}

/**
 * Formatiert eine Datumsspanne als String (z.B. "01.01.2024 - 05.01.2024").
 * @param {Date|string|number} sDate 
 * @param {Date|string|number} eDate 
 * @returns {string}
 */
export function formatDateRange(sDate, eDate) {
    if (!sDate || !eDate) return '';
    const sStr = format(new Date(sDate), 'dd.MM.yyyy');
    const eStr = format(new Date(eDate), 'dd.MM.yyyy');
    return sStr === eStr ? sStr : `${sStr} - ${eStr}`;
}

/**
 * Kurze Datumsspanne ohne Jahr (z.B. "01.01. - 05.01.").
 * @param {Date|string|number} sDate 
 * @param {Date|string|number} eDate 
 * @returns {string} 
 */
export function formatDateSimple(sDate, eDate) {
    if (!sDate || !eDate) return '';
    const sStr = format(new Date(sDate), 'dd.MM.');
    const eStr = format(new Date(eDate), 'dd.MM.');
    return sStr === eStr ? sStr : `${sStr} - ${eStr}`;
}

/**
 * Formatiert die sichtbare Datums-/Zeitangabe eines Einsatzes.
 * Ganztagseinträge bekommen eine klare Datumsspanne, damit Mehrtages-Blöcke
 * nicht nur mit ihrem Startdatum erscheinen.
 *
 * @param {Object} assignment - Einsatzobjekt mit start_datetime/end_datetime.
 * @returns {string} Lesbare Anzeige für Karten, Listen und Tooltips.
 */
export function formatAssignmentDateSpan(assignment) {
    if (!assignment?.start_datetime) return 'Kein Datum';

    const start = new Date(assignment.start_datetime);
    const end = new Date(assignment.end_datetime || assignment.start_datetime);
    const dateRange = formatDateRange(start, end);
    const isRange = !isSameDay(start, end);

    if (isAllDay(assignment)) {
        return isRange ? `${dateRange} · Ganztag` : `${dateRange} · Ganztag`;
    }

    const startTime = format(start, 'HH:mm');
    const endTime = format(end, 'HH:mm');
    return isRange ? `${dateRange} · ${startTime} - ${endTime}` : `${dateRange} · ${startTime} - ${endTime}`;
}

/**
 * Liste der Monatsnamen (Deutsch) für Kalender-Header.
 */
export const MONTH_NAMES = [
    'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
    'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
];

/**
 * Liste der Wochentage (Deutsch, index-basiert 0=So).
 */
export const DAY_NAMES = [
    'Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'
];
